import dayjs from 'dayjs'
import React from 'react'

import { CurrencyInput, Fieldset, Skeleton, Table, Tooltip } from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import { isPaidTier } from '../../../../utils'
import { useTierContext } from '../../hooks/useTierContext'
import { FormFields } from '../../providers/TierProvider/types'
import { getExchangeRate } from '../../utils'
import * as S from './styled'

export const CurrenciesTable = () => {
  const { tier, form, isPriceRatesLoading, priceRates, enabledCurrencies } = useTierContext()

  const getTableData = () => {
    if (!enabledCurrencies) return []
    if (tier && !('pricesAutomaticallyConverted' in tier)) return []

    return enabledCurrencies.map((currency) => {
      const getSuggestion = () => {
        if (!form.values.price) return undefined

        if (isPriceRatesLoading) return <Skeleton.Shape height={21} width={140} />

        if (!priceRates) return <S.Error>We couldn't load exchange rates.</S.Error>

        return `${form.values.price} USD = ${getExchangeRate(currency, priceRates)} ${currency}`
      }

      const getDisabled = () => {
        if (!tier) return false
        if (!isPaidTier(tier)) return false

        return tier.variants.monthly.amounts[
          currency.toLowerCase() as keyof typeof tier.variants.monthly.amounts
        ].locked
      }

      return {
        currency,
        disabled: getDisabled(),
        suggestion: getSuggestion(),
      }
    })
  }

  return (
    <Fieldset.Row inner>
      <Fieldset.Item>
        <Table
          data={getTableData()}
          emptyMessage="You haven't enabled any currencies yet."
          headers={[
            {
              id: 'price',
              label: 'Price per month',
              render: ({ currency, disabled }) => {
                if (disabled) {
                  return (
                    <Tooltip
                      placement="top"
                      reference={
                        <S.InputContainer>
                          <CurrencyInput
                            label={currency}
                            size="small"
                            prepend={currency}
                            disabled
                            {...form.getFieldProps(
                              `${FormFields.currencies}.${currency.toLowerCase()}`,
                            )}
                          />
                        </S.InputContainer>
                      }
                    >
                      Tier price in this currency cannot be changed when there are active members.
                    </Tooltip>
                  )
                }

                return (
                  <S.InputContainer>
                    <CurrencyInput
                      label={currency}
                      size="small"
                      prepend={currency}
                      disabled={disabled}
                      max={currency === 'JPY' ? 999999999 : 99999}
                      {...form.getFieldProps(`${FormFields.currencies}.${currency.toLowerCase()}`)}
                    />
                  </S.InputContainer>
                )
              },
              width: 2,
            },
            {
              id: 'suggestion',
              label: (
                <p>
                  Suggestion based on <TimezonedDateString date={dayjs()} format="MMM D, YYYY" />{' '}
                  exchange rate
                </p>
              ),
              render: ({ suggestion }) => <S.Suggestion>{suggestion}</S.Suggestion>,
              width: 5,
            },
          ]}
          minWidth={0}
          compact
        />
      </Fieldset.Item>
    </Fieldset.Row>
  )
}
